/**
 * Legal-hold toggle (legal-review follow-up 2026-10-25).
 *
 * Owner-only. Turning the hold on pauses Sill's automatic removal of
 * per-record IP addresses on this account's records and blocks account
 * deletion. Use it when legal action is expected. The hold is reversible
 * at any time.
 */

import { useCallback, useEffect, useState } from 'react';

import { useAuth } from '../../app/auth-context.js';
import { useToast } from '../../app/providers.js';
import { useOptionalHttp } from '../../api/http-context.js';
import {
  getLegalHoldStatus,
  turnOffLegalHold,
  turnOnLegalHold,
} from '../../api/account-legal-hold.js';
import type { AccountLegalHoldStatus } from '@sill/types/api';
import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";
const REASON_MAX = 500;

function sinceLabel(iso: string | null): string {
  if (iso === null) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toISOString().slice(0, 10);
}

export function LegalHoldPanel(): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const toast = useToast();
  const auth = useAuth();

  const user = auth.state.kind === 'authenticated' ? auth.state.user : null;
  const isOwner = user?.role === 'owner';

  const [status, setStatus] = useState<AccountLegalHoldStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async (): Promise<void> => {
    if (http === null) return;
    setLoading(true);
    const res = await getLegalHoldStatus(http);
    if (res.kind === 'ok') setStatus(res.value);
    setLoading(false);
  }, [http]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onTurnOn = async (): Promise<void> => {
    if (http === null) return;
    setBusy(true);
    const res = await turnOnLegalHold(http, reason.trim());
    setBusy(false);
    if (res.kind === 'ok') {
      setStatus(res.value);
      setReason('');
      toast({ tone: 'amber', title: 'Legal hold turned on for this account.' });
      return;
    }
    toast({ tone: 'coral', title: 'Could not turn the hold on.', body: res.message });
  };

  const onTurnOff = async (): Promise<void> => {
    if (http === null) return;
    setBusy(true);
    const res = await turnOffLegalHold(http);
    setBusy(false);
    if (res.kind === 'ok') {
      setStatus(res.value);
      toast({ tone: 'lime', title: 'Legal hold turned off.' });
      return;
    }
    toast({ tone: 'coral', title: 'Could not turn the hold off.', body: res.message });
  };

  if (loading) {
    return (
      <div style={{ fontFamily: sansFont, fontSize: 14, color: C.muted }}>
        Loading...
      </div>
    );
  }

  if (!isOwner) {
    return (
      <div style={{ fontFamily: sansFont, fontSize: 14, color: C.body, maxWidth: '60ch' }}>
        Only the account owner can change the legal hold on this account.
      </div>
    );
  }

  const on = status?.on === true;

  return (
    <div style={{ maxWidth: '68ch' }}>
      <div
        style={{
          border: `1px solid ${on ? C.amberFg ?? C.border : C.border}`,
          borderRadius: 4,
          padding: 20,
          background: 'transparent',
        }}
      >
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            letterSpacing: 2,
            textTransform: 'uppercase',
            color: on ? C.amberFg ?? C.muted : C.muted,
            marginBottom: 8,
          }}
        >
          Legal hold
        </div>
        <h3
          style={{
            fontFamily: sansFont,
            fontSize: 16,
            fontWeight: 600,
            color: C.text,
            margin: '0 0 8px',
          }}
        >
          {on ? 'Legal hold is on' : 'Legal hold is off'}
        </h3>
        <p style={{ fontFamily: sansFont, fontSize: 14, lineHeight: 1.6, color: C.body, marginBottom: 12 }}>
          A legal hold pauses Sill's automatic removal of IP addresses on records that matched an attack pattern, and blocks deletion of this account. Use it when you expect legal action.
        </p>
        {on ? (
          <>
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 11,
                color: C.muted,
                lineHeight: 1.6,
                background: C.surface2 ?? 'transparent',
                border: `1px solid ${C.border}`,
                borderRadius: 3,
                padding: '10px 12px',
                marginBottom: 12,
              }}
            >
              <div>On since <strong style={{ color: C.text }}>{sinceLabel(status?.since ?? null)}</strong></div>
              {status?.set_by_display !== null && status?.set_by_display !== undefined && status.set_by_display.length > 0 ? (
                <div>Turned on by <strong style={{ color: C.text }}>{status.set_by_display}</strong></div>
              ) : null}
              {status?.reason !== null && status?.reason !== undefined && status.reason.length > 0 ? (
                <div style={{ marginTop: 6 }}>Note: {status.reason}</div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => void onTurnOff()}
              disabled={busy}
              data-testid="legal-hold-turn-off"
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                fontWeight: 600,
                padding: '9px 16px',
                background: C.lime,
                color: C.ink,
                border: 'none',
                borderRadius: 3,
                cursor: 'pointer',
                opacity: busy ? 0.6 : 1,
              }}
            >
              Turn the hold off
            </button>
          </>
        ) : (
          <>
            <label
              style={{
                display: 'block',
                fontFamily: monoFont,
                fontSize: 10,
                color: C.muted,
                letterSpacing: 1.5,
                marginBottom: 4,
              }}
            >
              Reason (optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, REASON_MAX))}
              maxLength={REASON_MAX}
              placeholder="A short note for your own records, e.g. a case number."
              rows={3}
              style={{
                width: '100%',
                maxWidth: 480,
                background: C.surface2 ?? 'transparent',
                color: C.text,
                border: `1px solid ${C.border}`,
                borderRadius: 3,
                padding: '9px 12px',
                marginBottom: 10,
                fontFamily: sansFont,
                fontSize: 13,
                lineHeight: 1.5,
              }}
            />
            <button
              type="button"
              onClick={() => void onTurnOn()}
              disabled={busy}
              data-testid="legal-hold-turn-on"
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                fontWeight: 600,
                padding: '9px 16px',
                background: C.lime,
                color: C.ink,
                border: 'none',
                borderRadius: 3,
                cursor: busy ? 'not-allowed' : 'pointer',
                opacity: busy ? 0.6 : 1,
              }}
            >
              Turn the hold on
            </button>
          </>
        )}
      </div>
    </div>
  );
}
